import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import omit from "lodash/omit";
import isEmpty from "lodash/isEmpty";

import {
  getCartOrderBundleName,
  getCartOrderFetched,
  getCartOrderId,
  getCartOrderLineItems,
  getCartOrderVariantsMapping,
} from "@selectors/cart";

import { getIsLockerShop } from "@selectors/store";

import { usePreviewModeContext } from "@contexts/preview-mode-context";
import { useSendItemsContext } from "@contexts/send-items-context";

import useRefreshCumulativeCart from "@hooks/cart/use-refresh-cumulative-cart";

import { isSwagProduct } from "@helpers/products/types";
import { getUpdatedLineItemFromVariants } from "@helpers/orders/cart";

const defaultObj = {};

export const CartListingContext = React.createContext(defaultObj);

export const useCartListingContext = () => useContext(CartListingContext);

const emptyArr = [];
const emptyObj = {};

const CartListingProvider = ({ children }) => {
  const { isSendToOthers } = useSendItemsContext();

  const lineItems = useSelector(getCartOrderLineItems);
  const bundleName = useSelector(getCartOrderBundleName);
  const variantsMapping = useSelector(getCartOrderVariantsMapping);
  const orderId = useSelector(getCartOrderId);
  const orderFetched = useSelector(getCartOrderFetched);
  const isLockerShop = useSelector(getIsLockerShop);

  const { loading: isLoading, fetchAvailability } = useRefreshCumulativeCart();
  const { isGuestOrPreviewMode } = usePreviewModeContext();

  const [isSwagVariantSelectorModalOpen, setIsSwagVariantSelectorModalOpen] = useState(false);
  const [sendToOthersZoneWiseRecipient, setSendToOthersZoneWiseRecipient] = useState(emptyArr);
  const [sendToOtherItemName, setSendToOtherItemName] = useState(bundleName);
  const [variantSelectionError, setVariantSelectionError] = useState(emptyObj);

  useEffect(() => {
    const shouldFetchAvailability = isLockerShop
      ? orderId && !isLoading && !isGuestOrPreviewMode
      : orderFetched && orderId && !isLoading && !isGuestOrPreviewMode;

    if (shouldFetchAvailability) {
      fetchAvailability(orderId);
    }
  }, []);

  const openSwagVariantSelectorModal = () => setIsSwagVariantSelectorModalOpen(true);
  const closeSwagVariantSelectorModal = () => setIsSwagVariantSelectorModalOpen(false);

  const lineItemsVariantSelectionError = useMemo(() => {
    return omit(
      variantSelectionError,
      Object.keys(variantSelectionError).filter(
        (errorId) => !lineItems.some((item) => item.id === errorId),
      ),
    );
  }, [variantSelectionError, lineItems]);

  const updatedProductsWithVariant = (item) => {
    if (isGuestOrPreviewMode) return { ...item, masterVariant: item.product?.masterVariant };
    return getUpdatedLineItemFromVariants(item, variantsMapping, isSendToOthers);
  };

  const getSwagVariantsSelectionProducts = useCallback(
    () =>
      lineItems
        .map(updatedProductsWithVariant)
        .filter(
          ({
            intent = "",
            isSwagVariantSelectionAvailable,
            isMaster,
            askForPreference,
            dietaryPreference,
          }) =>
            isSwagProduct(intent) &&
            isMaster &&
            (isSwagVariantSelectionAvailable ||
              (!isSwagVariantSelectionAvailable && askForPreference && isEmpty(dietaryPreference))),
        ),
    [lineItems, variantsMapping],
  );

  const value = useMemo(
    () => ({
      isSwagVariantSelectorModalOpen,
      openSwagVariantSelectorModal,
      closeSwagVariantSelectorModal,
      sendToOthersZoneWiseRecipient,
      setSendToOthersZoneWiseRecipient,
      sendToOtherItemName,
      setSendToOtherItemName,
      variantSelectionError: lineItemsVariantSelectionError,
      setVariantSelectionError,
      getSwagVariantsSelectionProducts,
    }),
    [
      isSwagVariantSelectorModalOpen,
      openSwagVariantSelectorModal,
      closeSwagVariantSelectorModal,
      sendToOthersZoneWiseRecipient,
      setSendToOthersZoneWiseRecipient,
      sendToOtherItemName,
      setSendToOtherItemName,
      lineItemsVariantSelectionError,
      setVariantSelectionError,
      getSwagVariantsSelectionProducts,
    ],
  );
  return <CartListingContext.Provider value={value}>{children}</CartListingContext.Provider>;
};
export default CartListingProvider;
